/* import __COLOCATED_TEMPLATE__ from './blocks-as-text.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type IntlService from 'embercom/services/intl';
import { htmlToTextContent } from 'embercom/lib/html-unescape';
import {
  type BlockList,
  type Block,
  type Image,
  type AttachmentList,
  type MessengerCardBlock,
  type List,
  type Html,
} from '@intercom/interblocks.ts';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import type Session from 'embercom/services/session';
import type CustomEmojiService from 'embercom/services/custom-emoji-service';
import { translateBlock } from '../conversation-stream/helpers/translations';
import type Conversation from 'embercom/objects/inbox/conversation';

interface Args {
  blocks: BlockList;
  checkForTruncation?: boolean;
  highlights?: string[];
  conversation?: Conversation;
}

interface Signature {
  Args: Args;
}

const MAX_LENGTH = 500;

export default class BlocksAsText extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare session: Session;
  @service declare customEmojiService: CustomEmojiService;

  get hasCustomEmojis(): boolean {
    return this.text.includes('class="custom-emoji"');
  }

  get isCustomEmojisFeatureEnabled(): boolean {
    return this.session.workspace.isFeatureEnabled('custom_emojis');
  }

  private applyEmojiReplacement(text: string): string {
    // Replace custom emoji patterns like :emoji: with images
    let emojiPattern = /:([a-zA-Z0-9-]+):/g;
    text = text.replace(emojiPattern, (match, emojiName) => {
      let customEmoji = this.customEmojiService.getCustomEmoji(emojiName);
      if (customEmoji) {
        let escapedSrc = customEmoji.src.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        return `<img src="${escapedSrc}" class="custom-emoji" alt="">`;
      }
      return match;
    });
    return text;
  }

  get text() {
    if (!this.args.blocks) {
      return '';
    }

    let blocks = excludeReapeatedBlockTypes(this.args.blocks);
    let totalTextLength = 0;
    let textEntries = [];
    let autoTranslationEnabledForConversation =
      this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
        this.args.conversation,
      );

    for (let i = 0; i < blocks.length && totalTextLength < MAX_LENGTH; i++) {
      let block = blocks[i];
      let blockText = blockToText(
        block,
        this.intl,
        autoTranslationEnabledForConversation,
        this.session.workspace.locale,
      );
      totalTextLength += blockText.length;
      textEntries.push(blockText);
    }

    let text = textEntries.join(' ');

    // replace <br> with spaces, as they'll be entirely stripped out when using `htmlToTextContent`
    // and if the only HTML the string contains is <br> then this avoids the expensive `htmlToTextContent` call
    if (text.includes('<br>')) {
      text = text.replace(/<br>/g, ' ');
    }

    if (text.match(/<|>|&/g)) {
      // If the summary text contains any HTML content, we strip out the HTML using `htmlToTextContent`
      text = htmlToTextContent(text);
      return this.isCustomEmojisFeatureEnabled ? this.applyEmojiReplacement(text) : text;
    } else {
      // If it does not contain HTML characters we skip the HTML to text step as it's relatively expensive.
      return this.isCustomEmojisFeatureEnabled ? this.applyEmojiReplacement(text) : text;
    }
  }

  get plainTextBlocks() {
    return [
      {
        class: 'no-margin',
        type: 'paragraph',
        text: this.text,
      },
    ];
  }
}

export function blockToText(
  block: Block,
  intl: IntlService,
  toTranslate = false,
  lang?: string,
): string {
  if (toTranslate && lang) {
    block = translateBlock(block, toTranslate, lang);
  }
  switch (block.type) {
    case 'image':
      block = block as Image;
      if (!block.url) {
        return intl.t('inbox.common.block-as-text.sent-image');
      }
      if (block.url.includes('gifs.intercomcdn.com')) {
        return intl.t('inbox.common.block-as-text.sent-gif');
      }
      return `${intl.t('inbox.common.block-as-text.sent-image')} "${basename(block.url)}"`;
    case 'attachmentList': {
      block = block as AttachmentList;
      let attachments = block.attachments
        .map((attachment: any) => `"${attachment.name}"`)
        .join(', ');

      return `${intl.t('inbox.common.block-as-text.sent-attachment', {
        count: block.attachments.length,
      })} ${attachments}`;
    }
    case 'messengerCard':
      block = block as MessengerCardBlock;
      return `[${block.text}]`;
    case 'html':
      block = block as Html;
      return block.content;

    case 'unorderedList':
    case 'orderedList':
      block = block as List;
      return block.items.map((item) => item).join(' ');

    default:
      if ('text' in block) {
        return block.text || '';
      } else {
        return '';
      }
  }
}

function basename(str: string) {
  return new URL(str).pathname.split('/').pop();
}

function excludeReapeatedBlockTypes(blocks: Block[]) {
  return blocks.reduce((blocks: Block[], currentBlock) => {
    // If first block is paragraph we do not render image or attachmentList.
    // We do not render image or attachmentList if previous block was the same type.
    let previousBlock = blocks[blocks.length - 1 <= 0 ? 0 : blocks.length - 1];
    let nonDuplicatedBlocks = ['image', 'attachmentList'];
    let hasPreviousBlockPriority = [...nonDuplicatedBlocks, 'paragraph'].includes(
      previousBlock?.type,
    );
    let canRemoveCurrentBlock = nonDuplicatedBlocks.includes(currentBlock?.type);

    if (hasPreviousBlockPriority && canRemoveCurrentBlock) {
      return blocks;
    }

    return [...blocks, currentBlock];
  }, []);
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Common::BlocksAsText': typeof BlocksAsText;
    'inbox2/common/blocks-as-text': typeof BlocksAsText;
  }
}
